import React, { FC, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { uniqBy } from 'lodash';
import { Action, Dispatch } from 'redux';

import { PaymentProcessEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { ReportingCall } from '../../lib/models/reporting/types';
import { RootState } from '../../lib/store';
import { getLeaderboard as getLeaderboardCall } from '../../lib/store/reporting';
import { getCurrentAccount } from '../../lib/store/selectors';
import { isCallStatusError, isCallStatusPending, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import { useIsMobile } from '../../hooks';

import Position from './Position';
import Ranking from './Ranking';

import styles from './Leaderboard.module.scss';

type BaseTournamentProps = {
  isTournamentStarted: boolean;
  isTournamentEnded: boolean;
};

type Props = BaseTournamentProps & ReduxProps;

const Leaderboard: FC<Props> = (props: Props): JSX.Element => {
  const {
    isTournamentStarted,
    isLeaderboardPending,
    isLeaderboardReady,
    isLeaderboardError,
    getLeaderboard,
    isTournamentEnded,
    leaderboard,
    accountReferenceId,
    initialAdjustmentAmount,
  } = props;
  const { requestedCompetitors, competitors } = leaderboard;
  const isMobile = useIsMobile();

  const myPositionFromCompetitor = requestedCompetitors.find(competitor =>
    competitor.accountReferenceId === accountReferenceId
      && competitor.requestedAccountReferenceId === accountReferenceId);

  const uniqueCompetitors = uniqBy(competitors, 'accountReferenceId');

  useEffect(() => {
    if (isLeaderboardPending || isLeaderboardReady || isLeaderboardError) {
      return;
    }
    getLeaderboard();
  }, [isLeaderboardPending, isLeaderboardReady, isLeaderboardError]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.wrapper}>
      <span className={styles.title}>{lang.commonMyPosition()}</span>
      <div className={classNames(styles.positions, { [styles.positionsMob]: isMobile })}>
        <Position
          initialAdjustmentAmount={initialAdjustmentAmount}
          data={myPositionFromCompetitor}
          isLoading={isLeaderboardPending}
          // There is wrong check that tournament is not started
          // Mocking response in order to show leaderboard stats
          isTournamentStarted
        />
      </div>
      <span className={styles.title}>{lang.commonMyPositionRanking()}</span>
      <div className={styles.ranking}>
        <Ranking
          initialAdjustmentAmount={initialAdjustmentAmount}
          data={uniqueCompetitors}
          isTournamentEnded={isTournamentEnded}
          isTournamentStarted={isTournamentStarted}
          isLoading={isLeaderboardPending}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  initialAdjustmentAmount: state.payment.payments.data?.find(
    item => item.payment_process === PaymentProcessEnum.Adjustment,
  )?.amount ?? 0,
  leaderboard: state.reporting.leaderboard,
  accountReferenceId: getCurrentAccount(state),
  isLeaderboardPending: isCallStatusPending(ReportingCall.getLeaderboard, state.reporting.statusByCall),
  isLeaderboardReady: isCallStatusReady(ReportingCall.getLeaderboard, state.reporting.statusByCall),
  isLeaderboardError: isCallStatusError(ReportingCall.getLeaderboard, state.reporting.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getLeaderboard: () => dispatch(getLeaderboardCall()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Leaderboard);
